import { useCallback, useRef } from 'react'
import { message as antMessage } from 'antd'
import { useChatStore } from '@/store/chatStore'
import { SSEParser } from '@/utils/sse-parser'
import { API_CONFIG } from '@/config/constants'
import type { QueryRequest, SSEEventData, SSEEventDataBackend } from '@/types/api.types'
import type { FollowUpAction } from '@/types/chat.types'
import { t } from '@/config/translations'

const THROTTLE_MS = 100 // Batch UI updates every 100ms

// Type guard for FollowUpAction array
function isFollowUpActionArray(value: unknown): value is FollowUpAction[] {
  if (!Array.isArray(value)) return false
  return value.every((item: unknown) => {
    if (typeof item !== 'object' || item === null) return false
    const obj = item as Record<string, unknown>
    return (
      'label' in obj &&
      'type' in obj &&
      'payload' in obj &&
      typeof obj['label'] === 'string' &&
      (obj['type'] === 'chat_query' || obj['type'] === 'copy_text') &&
      typeof obj['payload'] === 'string'
    )
  })
}

interface UseStreamQueryReturn {
  sendQuery: (request: QueryRequest) => Promise<void>
  isStreaming: boolean
  abortStream: () => void
}

export function useStreamQuery(): UseStreamQueryReturn {
  const {
    addMessage,
    appendToMessageContent,
    updateMessageSources,
    updateMessageAgentActivity,
    updateMessageActions,
    setStreamingStatus,
    abortStreaming,
    streamingState,
  } = useChatStore()

  const throttleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingChunksRef = useRef<string>('')
  const agentActivityRef = useRef<string>('')

  // Throttled content append to batch UI updates
  const flushPendingChunks = useCallback(
    (messageId: string, immediate = false) => {
      if (immediate && throttleTimerRef.current) {
        clearTimeout(throttleTimerRef.current)
        throttleTimerRef.current = null
      }

      const flush = () => {
        if (pendingChunksRef.current) {
          appendToMessageContent(messageId, pendingChunksRef.current)
          pendingChunksRef.current = ''
        }
      }

      if (immediate) {
        flush()
      } else if (!throttleTimerRef.current) {
        throttleTimerRef.current = setTimeout(() => {
          throttleTimerRef.current = null
          flush()
        }, THROTTLE_MS)
      }
    },
    [appendToMessageContent]
  )

  const abortStream = useCallback(() => {
    abortStreaming()
    if (throttleTimerRef.current) {
      clearTimeout(throttleTimerRef.current)
      throttleTimerRef.current = null
    }
    pendingChunksRef.current = ''
    agentActivityRef.current = ''
    void antMessage.info(t('generation_stopped'))
  }, [abortStreaming])

  const sendQuery = useCallback(
    async (request: QueryRequest): Promise<void> => {
      // Add user message
      addMessage({
        role: 'user',
        content: request.query,
        scenario: request.scenario,
      })

      // Create assistant message placeholder
      const assistantMessage = addMessage({
        role: 'assistant',
        content: '',
      })

      // Create AbortController
      const controller = new AbortController()
      setStreamingStatus(true, assistantMessage.id, controller)

      const parser = new SSEParser()

      try {
        const response = await fetch(`${API_CONFIG.BASE_URL}/query/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(request),
          signal: controller.signal,
        })

        if (!response.ok) {
          throw new Error(t('connection_error', { error: `HTTP ${String(response.status)}` }))
        }

        if (!response.body) {
          throw new Error(t('connection_error', { error: 'No response body' }))
        }

        const reader = response.body.getReader()
        const decoder = new TextDecoder()

        let isReading = true
        while (isReading) {
          const { done, value } = await reader.read()

          if (done) {
            // Flush any pending chunks
            flushPendingChunks(assistantMessage.id, true)
            isReading = false
            continue
          }

          const chunk = decoder.decode(value, { stream: true })
          const events = parser.parse(chunk)

          for (const event of events) {
            if (!event.data) continue

            try {
              const data = JSON.parse(event.data) as SSEEventDataBackend | SSEEventData

              // Backend sends events without 'type' field, detect by structure
              if ('chunk' in data) {
                // This is a chunk event
                pendingChunksRef.current += data.chunk
                flushPendingChunks(assistantMessage.id)
              } else if ('done' in data) {
                // This is a done event
                flushPendingChunks(assistantMessage.id, true)
              } else if ('sources' in data) {
                // This is a sources event
                updateMessageSources(assistantMessage.id, data.sources)
              } else if ('agent_event' in data) {
                // This is an agent event with {type, data} structure
                const agentEvent = data.agent_event as {
                  type: string
                  data?: Record<string, unknown>
                }

                // Build markdown-formatted activity log
                let activityLine = ''

                switch (agentEvent.type) {
                  case 'agent_start':
                    activityLine = `🤖 **${t('agent_activity_started')}**\n\n`
                    break
                  case 'iteration_start': {
                    const iteration = agentEvent.data?.['iteration']
                    if (typeof iteration === 'number' || typeof iteration === 'string') {
                      activityLine = `\n---\n\n### ${t('agent_activity_iteration')} ${String(iteration)}\n\n`
                    }
                    break
                  }
                  case 'thinking':
                    // Just a marker that LLM is thinking - no content
                    activityLine = `💭 *${t('agent_activity_thinking')}...*\n\n`
                    break
                  case 'thought': {
                    // Backend sends: { thought: "text", iteration: 1 }
                    const thought = agentEvent.data?.['thought']
                    if (typeof thought === 'string' && thought) {
                      activityLine = `💡 **${t('agent_activity_thought')}:** ${thought}\n\n`
                    }
                    break
                  }
                  case 'action': {
                    // Backend sends: { tool: "hybrid_search", args: {...}, iteration: 1 }
                    const tool = agentEvent.data?.['tool']
                    const args = agentEvent.data?.['args']
                    let argsStr = ''
                    if (args && typeof args === 'object') {
                      // Show query or other relevant args
                      if ('query' in args) {
                        argsStr = `: "${String(args.query)}"`
                      }
                    }
                    if (typeof tool === 'string' && tool) {
                      activityLine = `⚡ **${t('agent_activity_action')}:** \`${tool}\`${argsStr}\n\n`
                    }
                    break
                  }
                  case 'observation': {
                    // Backend sends: { result: {...}, success: true, iteration: 1 }
                    const success = agentEvent.data?.['success'] === true
                    const result = agentEvent.data?.['result']
                    let resultSummary = ''
                    if (
                      typeof result === 'object' &&
                      result !== null &&
                      'results' in result &&
                      Array.isArray(result.results)
                    ) {
                      resultSummary = ` (${t('agent_activity_found_documents', { count: String(result.results.length) })})`
                    }
                    activityLine = `👁️ **${t('agent_activity_observation')}:** ${success ? `✅ ${t('agent_activity_success')}` : `❌ ${t('agent_activity_error')}`}${resultSummary}\n\n`
                    break
                  }
                  case 'planning_start':
                    activityLine = `📋 **${t('agent_activity_planning_start')}**\n\n`
                    break
                  case 'planning_complete': {
                    // Backend sends: { plan: {...}, steps_count: 5, informed: true }
                    const stepsCount = agentEvent.data?.['steps_count']
                    if (typeof stepsCount === 'number' && stepsCount > 0) {
                      activityLine = `✅ **${t('agent_activity_planning_complete')}** (${t('agent_activity_steps', { count: String(stepsCount) })})\n\n`
                    } else {
                      activityLine = `✅ **${t('agent_activity_planning_complete')}**\n\n`
                    }
                    break
                  }
                  case 'agent_finish': {
                    // Backend sends: { reason: "...", iteration: 3 }
                    const reason = agentEvent.data?.['reason']
                    if (typeof reason === 'string' && reason) {
                      activityLine = `\n---\n\n✅ **${t('agent_activity_complete')}:** ${reason}\n`
                    } else {
                      activityLine = `\n---\n\n✅ **${t('agent_activity_complete')}**\n`
                    }
                    break
                  }
                  case 'assessment':
                  case 'assessment_sufficient':
                    // Don't show assessment details to user - too technical
                    break
                  default:
                    // Ignore unknown event types silently
                    break
                }

                if (activityLine) {
                  agentActivityRef.current += activityLine
                  updateMessageAgentActivity(assistantMessage.id, agentActivityRef.current)
                }
              } else if ('actions' in data) {
                // This is a follow-up actions event
                if (isFollowUpActionArray(data.actions)) {
                  updateMessageActions(assistantMessage.id, data.actions)
                }
              } else if ('routing' in data) {
                // Routing info - can be silently ignored or logged if needed
              } else if ('error' in data) {
                const errorMessage = 'message' in data && typeof data.message === 'string'
                  ? data.message
                  : data.error
                flushPendingChunks(assistantMessage.id, true)
                appendToMessageContent(assistantMessage.id, `\n\n${errorMessage}`)
                void antMessage.error(errorMessage)
                await reader.cancel()
                isReading = false
                break
              }
            } catch (parseError) {
              console.error('[SSE] Failed to parse event data:', parseError)
            }
          }
        }

        // Success
        setStreamingStatus(false, null, null)
      } catch (error) {
        // Handle abort vs real error
        if (error instanceof Error) {
          if (error.name === 'AbortError') {
            // User cancelled - silent
            setStreamingStatus(false, null, null)
            return
          }

          // Real error
          flushPendingChunks(assistantMessage.id, true)
          appendToMessageContent(assistantMessage.id, t('connection_error', { error: error.message }))
          void antMessage.error(t('connection_error', { error: error.message }))
          console.error('[SSE] Stream error:', error)
        }

        setStreamingStatus(false, null, null)
      } finally {
        // Cleanup
        if (throttleTimerRef.current) {
          clearTimeout(throttleTimerRef.current)
          throttleTimerRef.current = null
        }
        pendingChunksRef.current = ''
        agentActivityRef.current = ''
      }
    },
    [
      addMessage,
      appendToMessageContent,
      updateMessageSources,
      updateMessageAgentActivity,
      updateMessageActions,
      setStreamingStatus,
      flushPendingChunks,
    ]
  )

  return {
    sendQuery,
    isStreaming: streamingState.isStreaming,
    abortStream,
  }
}
